/**
 * Character creation — **the missing entry point.** Until this file existed, a new
 * player could sign in to the deployed site and had no way to start a game at all: `POST
 * /stories` had no UI, so `wasteland.jokersoft.net` was a working application with no door.
 *
 * Four questions and a button. What deserves comment is not the form, it is what happens
 * after it:
 *
 * **Creation and the opening turn are two calls, and that is the point.** One call that made a story and
 * its first chapter in one request, so the player watched a "creating story" spinner for the
 * ~50 seconds the DM spent writing, with nothing to read and no way to tell a slow turn from
 * a dead one. v2 splits them (P4): `POST /stories` returns in milliseconds with a story that
 * exists, and the opening turn is *the same streamed turn as every other one*, rendered by
 * the same feed, with the same phase line and the same recovery paths. So this route's last
 * act is to navigate into `/play/{id}` and let `PlayRoute` run it.
 *
 * The turn is not started here. It is started by the play route, off `location.state`, for a
 * reason worth keeping: a turn started on this screen would be a turn whose stream is owned
 * by a component that is about to unmount, and the turn store lives at module scope
 * precisely so that never has to work.
 *
 * **No opening text is sent** — `{"action": null}` and nothing else. The opening turn: the API substitutes
 * a random first-person line from `prompts.opening.01`–`.20` in the story's own locale, and
 * that namespace is deliberately not served to the client.
 */
import { useId, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { describeApiError } from '@/api/errorMessages';
import { useCreateStory } from '@/api/queries';
import { LOCALES } from '@/features/settings/locales';
import { useT } from '@/i18n/context';
import {
  AGE_MAX,
  AGE_MIN,
  NAME_MAX,
  SEXES,
  draftErrors,
  emptyDraft,
  isSubmittable,
  toStoryCreate,
  type CharacterDraft,
  type DraftError,
} from '@/features/stories/characterDraft';
import { Button } from '@/ui/Button';

interface CharacterCreationProps {
  /** The player's own locale, which is the default the API would apply anyway. */
  defaultLocale: string;
}

export function CharacterCreation({ defaultLocale }: CharacterCreationProps) {
  const t = useT();
  const navigate = useNavigate();
  const create = useCreateStory();

  const [draft, setDraft] = useState<CharacterDraft>(() => emptyDraft(defaultLocale));
  // Errors are shown after a submit attempt, not while the player is still typing the first
  // letter of a name. One boolean, rather than per-field touched state, because the form is
  // four fields and submitting is the only way to leave it.
  const [attempted, setAttempted] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const errors = draftErrors(draft);
  const showErrors = attempted;

  const submit = (): void => {
    setAttempted(true);
    setFailure(null);
    if (!isSubmittable(draft) || create.isPending) return;

    create.mutate(toStoryCreate(draft), {
      onSuccess: (created) => {
        // `autoStart` is what makes the opening turn immediate rather than a second click.
        // The play route still renders "wake up" for a story reached any other way — a
        // creation whose opening turn failed is exactly that story, and it must stay startable.
        void navigate(`/play/${created.story_id}`, { state: { autoStart: true }, replace: true });
      },
      onError: (error: unknown) => {
        setFailure(describeApiError(t, error));
      },
    });
  };

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      noValidate
    >
      <Field
        label={t('ui.character.name')}
        hint={t('ui.character.name_hint', { max: NAME_MAX })}
        error={showErrors ? errors.name : undefined}
      >
        {(id, describedBy) => (
          <input
            id={id}
            name="character_name"
            type="text"
            value={draft.name}
            autoComplete="off"
            maxLength={NAME_MAX}
            aria-describedby={describedBy}
            aria-invalid={showErrors && errors.name !== undefined}
            onChange={(event) => {
              setDraft({ ...draft, name: event.target.value });
            }}
            className="border-border focus:border-accent text-fg placeholder:text-fg-faint w-full border bg-transparent px-3 py-2 font-mono transition-colors outline-none"
            style={{ fontSize: 'var(--text-action)' }}
          />
        )}
      </Field>

      {/*
       * A radio group, not a select. Three options is under the threshold where a menu earns
       * its extra click, and the engine only ever reads this to pick pronouns in the prompt —
       * so it is a fact about the character, not a setting.
       */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-fg-muted font-mono" style={{ fontSize: 'var(--text-meta)' }}>
          {t('ui.character.sex')}
        </legend>
        <div className="mt-1 flex flex-wrap gap-2">
          {SEXES.map((sex) => (
            <label
              key={sex}
              className={`cursor-pointer border px-3 py-1.5 font-mono transition-colors ${
                draft.sex === sex
                  ? 'border-accent text-accent'
                  : 'border-border text-fg-muted hover:border-fg-faint'
              }`}
              style={{ fontSize: 'var(--text-meta)' }}
            >
              <input
                type="radio"
                name="character_sex"
                value={sex}
                checked={draft.sex === sex}
                onChange={() => {
                  setDraft({ ...draft, sex });
                }}
                className="sr-only"
              />
              {t(`ui.character.sex.${sex}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <Field
        label={t('ui.character.age')}
        hint={t('ui.character.age_hint', { min: AGE_MIN, max: AGE_MAX })}
        error={showErrors ? errors.age : undefined}
      >
        {(id, describedBy) => (
          <input
            id={id}
            name="character_age"
            type="number"
            inputMode="numeric"
            value={draft.age}
            min={AGE_MIN}
            max={AGE_MAX}
            aria-describedby={describedBy}
            aria-invalid={showErrors && errors.age !== undefined}
            onChange={(event) => {
              setDraft({ ...draft, age: event.target.value });
            }}
            className="border-border focus:border-accent text-fg w-28 border bg-transparent px-3 py-2 font-mono transition-colors outline-none"
            style={{ fontSize: 'var(--text-action)' }}
          />
        )}
      </Field>

      <Field label={t('ui.character.language')} hint={t('ui.character.language_hint')}>
        {(id, describedBy) => (
          <select
            id={id}
            name="locale"
            value={draft.locale ?? defaultLocale}
            aria-describedby={describedBy}
            onChange={(event) => {
              setDraft({ ...draft, locale: event.target.value });
            }}
            className="border-border focus:border-accent text-fg border bg-transparent px-3 py-2 font-mono transition-colors outline-none"
            style={{ fontSize: 'var(--text-action)' }}
          >
            {LOCALES.map((locale) => (
              <option key={locale.code} value={locale.code} className="bg-panel">
                {locale.label}
              </option>
            ))}
          </select>
        )}
      </Field>

      {failure ? (
        <p
          role="alert"
          className="text-[color:var(--color-ramp-danger)]"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {failure}
        </p>
      ) : null}

      <Button type="submit" variant="primary" size="action" disabled={create.isPending}>
        {create.isPending ? t('ui.character.waking') : t('ui.character.begin')}
      </Button>
    </form>
  );
}

/**
 * Label, control, hint and error as one block, with the ids wired. A render prop rather than
 * `children`, because the whole reason this exists is that the control needs the generated
 * `id` and `aria-describedby` — passing them down is the job.
 */
function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: DraftError;
  children: (id: string, describedBy: string | undefined) => ReactNode;
}) {
  const t = useT();
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="text-fg-muted font-mono"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {label}
      </label>
      {children(id, describedBy)}
      {hint ? (
        <p id={hintId} className="text-fg-faint" style={{ fontSize: 'var(--text-meta)' }}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p
          id={errorId}
          className="text-[color:var(--color-ramp-danger)]"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          {t(error.key, error.params)}
        </p>
      ) : null}
    </div>
  );
}
